import React, { useState, useEffect } from 'react';
import {
    Layout,
    Card,
    Button,
    Space,
    message,
    Popconfirm,
    Tag,
    Typography,
    Menu
} from 'antd';
import Table from 'components/Common/Table';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    DollarOutlined,
    GiftOutlined,
    TrophyOutlined,
    DollarCircleOutlined,
    MinusCircleOutlined,
    EyeOutlined,
    CopyOutlined
} from '@ant-design/icons';
import * as adminactions from 'store/actions/AuthActions';
import BasicSalaryModal from './Modals/BasicSalaryModal';
import AllowanceModal from './Modals/AllowanceModal';
import CommissionModal from './Modals/CommissionModal';
import OtherPaymentModal from './Modals/OtherPaymentModal';
import DeductionModal from './Modals/DeductionModal';
import SalaryPreviewModal from './Modals/SalaryPreviewModal';

const { Title, Text } = Typography;
const { Sider, Content } = Layout;

// Helper function to format dates consistently as "Dec 16, 2025"
const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const SalaryInfoTab = ({ employeeId }) => {
    const [activeSection, setActiveSection] = useState('basic-salary');

    // State for each salary component
    const [basicSalaries, setBasicSalaries] = useState([]);
    const [allowances, setAllowances] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [otherPayments, setOtherPayments] = useState([]);
    const [deductions, setDeductions] = useState([]);

    // Modal states
    const [basicSalaryModalVisible, setBasicSalaryModalVisible] = useState(false);
    const [allowanceModalVisible, setAllowanceModalVisible] = useState(false);
    const [commissionModalVisible, setCommissionModalVisible] = useState(false);
    const [otherPaymentModalVisible, setOtherPaymentModalVisible] = useState(false);
    const [deductionModalVisible, setDeductionModalVisible] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);

    // Editing record states
    const [editingBasicSalary, setEditingBasicSalary] = useState(null);
    const [editingAllowance, setEditingAllowance] = useState(null);
    const [editingCommission, setEditingCommission] = useState(null);
    const [editingOtherPayment, setEditingOtherPayment] = useState(null);
    const [editingDeduction, setEditingDeduction] = useState(null);

    // Current basic salary
    const [currentBasicSalary, setCurrentBasicSalary] = useState(null);

    // Employee data (for joining date validation)
    const [employeeData, setEmployeeData] = useState(null);

    // Loading states
    const [loadingBasicSalary, setLoadingBasicSalary] = useState(false);
    const [loadingAllowances, setLoadingAllowances] = useState(false);
    const [loadingCommissions, setLoadingCommissions] = useState(false);
    const [loadingOtherPayments, setLoadingOtherPayments] = useState(false);
    const [loadingDeductions, setLoadingDeductions] = useState(false);

    useEffect(() => {
        if (employeeId) {
            loadAllSalaryData();
        }
    }, [employeeId]);

    const loadAllSalaryData = async () => {
        await Promise.all([
            loadBasicSalaries(),
            loadAllowances(),
            loadCommissions(),
            loadOtherPayments(),
            loadDeductions(),
            loadCurrentBasicSalary(),
            loadEmployeeData()
        ]);
    };

    const loadEmployeeData = async () => {
        try {
            const response = await adminactions.retrieveEmployee(employeeId);
            if (response) {
                setEmployeeData(response);
            }
        } catch (error) {
            console.error('Error loading employee data:', error);
        }
    };

    const loadCurrentBasicSalary = async () => {
        try {
            const response = await adminactions.getCurrentBasicSalary(employeeId);
            if (response && !response.message) {
                setCurrentBasicSalary(response);
            }
        } catch (error) {
            console.error('Error loading current basic salary:', error);
        }
    };

    const loadBasicSalaries = async () => {
        setLoadingBasicSalary(true);
        try {
            const response = await adminactions.loadBasicSalaries(employeeId);
            if (response && response.results) {
                setBasicSalaries(response.results);
            }
        } catch (error) {
            message.error('Failed to load basic salaries');
        } finally {
            setLoadingBasicSalary(false);
        }
    };

    const loadAllowances = async () => {
        setLoadingAllowances(true);
        try {
            const response = await adminactions.loadAllowances(employeeId);
            if (response && response.results) {
                setAllowances(response.results);
            }
        } catch (error) {
            message.error('Failed to load allowances');
        } finally {
            setLoadingAllowances(false);
        }
    };

    const loadCommissions = async () => {
        setLoadingCommissions(true);
        try {
            const response = await adminactions.loadCommissions(employeeId);
            if (response && response.results) {
                setCommissions(response.results);
            }
        } catch (error) {
            message.error('Failed to load commissions');
        } finally {
            setLoadingCommissions(false);
        }
    };

    const loadOtherPayments = async () => {
        setLoadingOtherPayments(true);
        try {
            const response = await adminactions.loadOtherPayments(employeeId);
            if (response && response.results) {
                setOtherPayments(response.results);
            }
        } catch (error) {
            message.error('Failed to load other payments');
        } finally {
            setLoadingOtherPayments(false);
        }
    };

    const loadDeductions = async () => {
        setLoadingDeductions(true);
        try {
            const response = await adminactions.loadDeductions(employeeId);
            if (response && response.results) {
                setDeductions(response.results);
            }
        } catch (error) {
            message.error('Failed to load deductions');
        } finally {
            setLoadingDeductions(false);
        }
    };

    // Delete handlers
    const handleDeleteBasicSalary = async (id) => {
        try {
            await adminactions.deleteBasicSalary(id);
            message.success('Basic salary deleted successfully');
            await loadBasicSalaries();
            await loadCurrentBasicSalary();
        } catch (error) {
            message.error('Failed to delete basic salary');
        }
    };

    const handleDeleteAllowance = async (id) => {
        try {
            await adminactions.deleteAllowance(id);
            message.success('Allowance deleted successfully');
            await loadAllowances();
        } catch (error) {
            message.error('Failed to delete allowance');
        }
    };

    const handleDeleteCommission = async (id) => {
        try {
            await adminactions.deleteCommission(id);
            message.success('Commission deleted successfully');
            await loadCommissions();
        } catch (error) {
            message.error('Failed to delete commission');
        }
    };

    const handleDeleteOtherPayment = async (id) => {
        try {
            await adminactions.deleteOtherPayment(id);
            message.success('Other payment deleted successfully');
            await loadOtherPayments();
        } catch (error) {
            message.error('Failed to delete other payment');
        }
    };

    const handleDeleteDeduction = async (id) => {
        try {
            await adminactions.deleteDeduction(id);
            message.success('Deduction deleted successfully');
            await loadDeductions();
        } catch (error) {
            message.error('Failed to delete deduction');
        }
    };

    // Clone handlers - using create APIs to clone records
    const handleCloneBasicSalary = async (record) => {
        try {
            const cloneData = {
                employee: employeeId,
                effective_date: record.effective_date,
                basic_salary: record.basic_salary,
                hourly_rate: record.hourly_rate,
                ot_rate: record.ot_rate,
            };
            const clonedRecord = await adminactions.createBasicSalary(cloneData);
            if (clonedRecord && clonedRecord.id) {
                message.success('Basic salary cloned successfully');
                await loadBasicSalaries();
                await loadCurrentBasicSalary();
                setEditingBasicSalary(clonedRecord);
                setBasicSalaryModalVisible(true);
            } else {
                message.error('Failed to clone basic salary');
            }
        } catch (error) {
            message.error('Failed to clone basic salary');
        }
    };

    const handleCloneAllowance = async (record) => {
        try {
            const cloneData = {
                employee: employeeId,
                effective_date: record.effective_date,
                allowance_type_id: record.allowance_type?.id || record.allowance_type,
                allowance_name: record.allowance_name,
                calculation_type: record.calculation_type,
                amount: record.amount,
                end_date: record.end_date,
                is_attendance_based: record.is_attendance_based,
            };
            const clonedRecord = await adminactions.createAllowance(cloneData);
            if (clonedRecord && clonedRecord.id) {
                message.success('Allowance cloned successfully');
                await loadAllowances();
                setEditingAllowance(clonedRecord);
                setAllowanceModalVisible(true);
            } else {
                message.error('Failed to clone allowance');
            }
        } catch (error) {
            message.error('Failed to clone allowance');
        }
    };

    const handleCloneCommission = async (record) => {
        try {
            const cloneData = {
                employee: employeeId,
                effective_date: record.effective_date,
                commission_title: record.commission_title,
                commission_amount: record.commission_amount,
            };
            const clonedRecord = await adminactions.createCommission(cloneData);
            if (clonedRecord && clonedRecord.id) {
                message.success('Commission cloned successfully');
                await loadCommissions();
                setEditingCommission(clonedRecord);
                setCommissionModalVisible(true);
            } else {
                message.error('Failed to clone commission');
            }
        } catch (error) {
            message.error('Failed to clone commission');
        }
    };

    const handleCloneOtherPayment = async (record) => {
        try {
            const cloneData = {
                employee: employeeId,
                effective_date: record.effective_date,
                payment_type_fk_id: record.payment_type_fk?.id || record.payment_type_fk,
                title: record.title,
                amount: record.amount,
            };
            const clonedRecord = await adminactions.createOtherPayment(cloneData);
            if (clonedRecord && clonedRecord.id) {
                message.success('Other payment cloned successfully');
                await loadOtherPayments();
                setEditingOtherPayment(clonedRecord);
                setOtherPaymentModalVisible(true);
            } else {
                message.error('Failed to clone other payment');
            }
        } catch (error) {
            message.error('Failed to clone other payment');
        }
    };

    const handleCloneDeduction = async (record) => {
        try {
            const cloneData = {
                employee: employeeId,
                effective_date: record.effective_date,
                deduction_type_fk_id: record.deduction_type_fk?.id || record.deduction_type_fk,
                deduction_name: record.deduction_name,
                amount: record.amount,
                end_date: record.end_date,
                is_attendance_based: record.is_attendance_based,
            };
            const clonedRecord = await adminactions.createDeduction(cloneData);
            if (clonedRecord && clonedRecord.id) {
                message.success('Deduction cloned successfully');
                await loadDeductions();
                setEditingDeduction(clonedRecord);
                setDeductionModalVisible(true);
            } else {
                message.error('Failed to clone deduction');
            }
        } catch (error) {
            message.error('Failed to clone deduction');
        }
    };

    // Menu items for sidebar
    const menuItems = [
        {
            key: 'basic-salary',
            icon: <DollarOutlined />,
            label: `Basic Salary (${basicSalaries.length})`
        },
        {
            key: 'allowances',
            icon: <GiftOutlined />,
            label: `Allowances (${allowances.length})`
        },
        {
            key: 'commissions',
            icon: <TrophyOutlined />,
            label: `Commissions (${commissions.length})`
        },
        {
            key: 'other-payments',
            icon: <DollarCircleOutlined />,
            label: `Other Payments (${otherPayments.length})`
        },
        {
            key: 'deductions',
            icon: <MinusCircleOutlined />,
            label: `Deductions (${deductions.length})`
        },
    ];

    // Table columns
    const basicSalaryColumns = [
        {
            title: 'Effective Date',
            dataIndex: 'effective_date',
            key: 'effective_date',
            render: (text, record) => (
                <span>
                    {formatDate(text)}
                    {currentBasicSalary?.id === record.id && (
                        <Tag color="green" style={{ marginLeft: 8 }}>Current</Tag>
                    )}
                </span>
            )
        },
        {
            title: 'Basic Salary',
            dataIndex: 'basic_salary',
            key: 'basic_salary',
            render: (amount) => `₹${parseFloat(amount).toLocaleString()}`
        },
        {
            title: 'Hourly Rate',
            dataIndex: 'hourly_rate',
            key: 'hourly_rate'
        },
        {
            title: 'OT Rate',
            dataIndex: 'ot_rate',
            key: 'ot_rate',
            render: (amount) => `₹${parseFloat(amount).toFixed(2)}`
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Modified At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                            setEditingBasicSalary(record);
                            setBasicSalaryModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title="Are you sure to create a clone?"
                        onConfirm={() => handleCloneBasicSalary(record)}
                    >
                        <Button icon={<CopyOutlined />} size="small" />
                    </Popconfirm>
                    <Popconfirm
                        title="Are you sure you want to delete this basic salary?"
                        onConfirm={() => handleDeleteBasicSalary(record.id)}
                    >
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const allowanceColumns = [
        {
            title: 'Effective Date',
            dataIndex: 'effective_date',
            key: 'effective_date',
            render: (text) => formatDate(text)
        },
        {
            title: 'Type',
            dataIndex: 'allowance_type_display',
            key: 'allowance_type_display',
            render: (amount, record) => record.allowance_type?.name || record.allowance_type_display || '-'
        },
        {
            title: 'Name',
            dataIndex: 'allowance_name',
            key: 'allowance_name',
            render: (text) => text || '-'
        },
        {
            title: 'Calculation',
            dataIndex: 'calculation_type_display',
            key: 'calculation_type_display'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) =>
                record.calculation_type === 'percentage'
                    ? `${amount}%`
                    : `₹${parseFloat(amount).toLocaleString()}`
        },
        {
            title: 'End Date',
            dataIndex: 'end_date',
            key: 'end_date',
            render: (endDate) => formatDate(endDate)
        },
        {
            title: 'Attendance Based',
            dataIndex: 'is_attendance_based',
            key: 'is_attendance_based',
            render: (is_attendance_based) => is_attendance_based ? "True" : 'False'
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Modified At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                            setEditingAllowance(record);
                            setAllowanceModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title="Are you sure to create a clone?"
                        onConfirm={() => handleCloneAllowance(record)}
                    >
                        <Button icon={<CopyOutlined />} size="small" />
                    </Popconfirm>
                    <Popconfirm
                        title="Are you sure you want to delete this allowance?"
                        onConfirm={() => handleDeleteAllowance(record.id)}
                    >
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const commissionColumns = [
        {
            title: 'Effective Date',
            dataIndex: 'effective_date',
            key: 'effective_date',
            render: (text) => formatDate(text)
        },
        {
            title: 'Title',
            dataIndex: 'commission_title',
            key: 'commission_title'
        },
        {
            title: 'Amount',
            dataIndex: 'commission_amount',
            key: 'commission_amount',
            render: (amount) => `₹${parseFloat(amount).toLocaleString()}`
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Modified At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                            setEditingCommission(record);
                            setCommissionModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title="Are you sure to create a clone?"
                        onConfirm={() => handleCloneCommission(record)}
                    >
                        <Button icon={<CopyOutlined />} size="small" />
                    </Popconfirm>
                    <Popconfirm
                        title="Are you sure you want to delete this commission?"
                        onConfirm={() => handleDeleteCommission(record.id)}
                    >
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const otherPaymentColumns = [
        {
            title: 'Effective Date',
            dataIndex: 'effective_date',
            key: 'effective_date',
            render: (text) => formatDate(text)
        },
        {
            title: 'Type',
            dataIndex: 'payment_type_fk',
            key: 'payment_type_fk',
            render: (payment_type_fk, record) => payment_type_fk?.name || record.payment_type_display || '-'
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `₹${parseFloat(amount).toLocaleString()}`
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Modified At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (updated_at) => formatDate(updated_at)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                            setEditingOtherPayment(record);
                            setOtherPaymentModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title="Are you sure to create a clone?"
                        onConfirm={() => handleCloneOtherPayment(record)}
                    >
                        <Button icon={<CopyOutlined />} size="small" />
                    </Popconfirm>
                    <Popconfirm
                        title="Are you sure you want to delete this payment?"
                        onConfirm={() => handleDeleteOtherPayment(record.id)}
                    >
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const deductionColumns = [
        {
            title: 'Effective Date',
            dataIndex: 'effective_date',
            key: 'effective_date',
            render: (text) => formatDate(text)
        },
        {
            title: 'Type',
            dataIndex: 'deduction_type_display',
            key: 'deduction_type_display',
            render: (amount, record) => record.deduction_type_fk?.name || record.deduction_type_display || '-'
        },
        {
            title: 'Name',
            dataIndex: 'deduction_name',
            key: 'deduction_name',
            render: (text) => text || '-'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `₹${parseFloat(amount).toLocaleString()}`
        },
        {
            title: 'End Date',
            dataIndex: 'end_date',
            key: 'end_date',
            render: (endDate) => formatDate(endDate)
        },
        {
            title: 'Attendance Based',
            dataIndex: 'is_attendance_based',
            key: 'is_attendance_based',
            render: (is_attendance_based) => is_attendance_based ? "True" : 'False'
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Modified At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text) => formatDate(text)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                            setEditingDeduction(record);
                            setDeductionModalVisible(true);
                        }}
                    />
                    <Popconfirm
                        title="Are you sure to create a clone?"
                        onConfirm={() => handleCloneDeduction(record)}
                    >
                        <Button icon={<CopyOutlined />} size="small" />
                    </Popconfirm>
                    <Popconfirm
                        title="Are you sure you want to delete this deduction?"
                        onConfirm={() => handleDeleteDeduction(record.id)}
                    >
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const getSectionTitle = () => {
        const titles = {
            'basic-salary': 'Basic Salary',
            'allowances': 'Allowances',
            'commissions': 'Commissions',
            'other-payments': 'Other Payments',
            'deductions': 'Statutory Deductions'
        };
        return titles[activeSection] || 'Salary Information';
    };

    const renderBasicSalarySection = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                {currentBasicSalary && (
                    <Card style={{ flex: 1, marginRight: 16, background: '#f0f5ff', borderColor: '#40a9ff' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <DollarOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 12 }} />
                            <div>
                                <Text type="secondary">Current Basic Salary</Text>
                                <Title level={4} style={{ margin: 0 }}>
                                    ₹{parseFloat(currentBasicSalary.basic_salary).toLocaleString()}
                                </Title>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Effective from {formatDate(currentBasicSalary.effective_date)}
                                </Text>
                            </div>
                        </div>
                    </Card>
                )}
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                        setEditingBasicSalary(null);
                        setBasicSalaryModalVisible(true);
                    }}
                >
                    Add Basic Salary
                </Button>
            </div>
            <Table
                columns={basicSalaryColumns}
                dataSource={basicSalaries}
                rowKey="id"
                loading={loadingBasicSalary}
                pagination={false}
                size="small"
            />
        </>
    );

    const renderAllowancesSection = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingAllowance(null);
                        setAllowanceModalVisible(true);
                    }}
                >
                    Add Allowance
                </Button>
            </div>
            <Table
                columns={allowanceColumns}
                dataSource={allowances}
                rowKey="id"
                loading={loadingAllowances}
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                size="small"
            />
        </>
    );

    const renderCommissionsSection = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingCommission(null);
                        setCommissionModalVisible(true);
                    }}
                >
                    Add Commission
                </Button>
            </div>
            <Table
                columns={commissionColumns}
                dataSource={commissions}
                rowKey="id"
                loading={loadingCommissions}
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                size="small"
            />
        </>
    );

    const renderOtherPaymentsSection = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingOtherPayment(null);
                        setOtherPaymentModalVisible(true);
                    }}
                >
                    Add Payment
                </Button>
            </div>
            <Table
                columns={otherPaymentColumns}
                dataSource={otherPayments}
                rowKey="id"
                loading={loadingOtherPayments}
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                size="small"
            />
        </>
    );

    const renderDeductionsSection = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingDeduction(null);
                        setDeductionModalVisible(true);
                    }}
                >
                    Add Deduction
                </Button>
            </div>
            <Table
                columns={deductionColumns}
                dataSource={deductions}
                rowKey="id"
                loading={loadingDeductions}
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                size="small"
            />
        </>
    );

    const renderContent = () => {
        switch (activeSection) {
            case 'basic-salary':
                return renderBasicSalarySection();
            case 'allowances':
                return renderAllowancesSection();
            case 'commissions':
                return renderCommissionsSection();
            case 'other-payments':
                return renderOtherPaymentsSection();
            case 'deductions':
                return renderDeductionsSection();
            default:
                return <Text>Section not found</Text>;
        }
    };

    return (
        <div style={{ height: 'calc(100vh - 280px)', padding: '20px' }}>
            <Card
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, padding: 0, overflow: 'hidden' }}
            >
                <Layout style={{ flex: 1, background: '#fff', overflow: 'hidden' }}>
                    {/* Sidebar */}
                    <Sider
                        width={220}
                        style={{
                            background: '#fff',
                            borderRight: '1px solid #f0f0f0',
                            overflow: 'auto'
                        }}
                    >
                        <style>
                            {`
                                .salary-sidebar-menu .ant-menu-item-selected {
                                    background-color: #1890ff !important;
                                    color: #fff !important;
                                }
                                .salary-sidebar-menu .ant-menu-item-selected .anticon {
                                    color: #fff !important;
                                }
                                .salary-sidebar-menu .ant-menu-item-selected::after {
                                    border-right-color: #1890ff !important;
                                }
                            `}
                        </style>
                        <Menu
                            mode="inline"
                            selectedKeys={[activeSection]}
                            onClick={({ key }) => setActiveSection(key)}
                            style={{ borderRight: 0 }}
                            items={menuItems}
                            className="salary-sidebar-menu"
                        />
                    </Sider>

                    {/* Content Area */}
                    <Content style={{ padding: '24px', overflow: 'auto', background: '#fff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                            <Title level={5} style={{ margin: 0 }}>
                                {getSectionTitle()}
                            </Title>
                            <Button
                                type="primary"
                                icon={<EyeOutlined />}
                                onClick={() => setPreviewModalVisible(true)}
                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Preview Salary
                            </Button>
                        </div>
                        {renderContent()}
                    </Content>
                </Layout>
            </Card>

            {/* Modals */}
            <BasicSalaryModal
                visible={basicSalaryModalVisible}
                onCancel={() => {
                    setBasicSalaryModalVisible(false);
                    setEditingBasicSalary(null);
                }}
                onSuccess={() => {
                    setBasicSalaryModalVisible(false);
                    setEditingBasicSalary(null);
                    loadBasicSalaries();
                    loadCurrentBasicSalary();
                }}
                employeeId={employeeId}
                editingRecord={editingBasicSalary}
                dateOfJoining={employeeData?.date_of_joining}
            />

            <AllowanceModal
                visible={allowanceModalVisible}
                onCancel={() => {
                    setAllowanceModalVisible(false);
                    setEditingAllowance(null);
                }}
                onSuccess={() => {
                    setAllowanceModalVisible(false);
                    setEditingAllowance(null);
                    loadAllowances();
                }}
                employeeId={employeeId}
                editingRecord={editingAllowance}
                dateOfJoining={employeeData?.date_of_joining}
            />

            <CommissionModal
                visible={commissionModalVisible}
                onCancel={() => {
                    setCommissionModalVisible(false);
                    setEditingCommission(null);
                }}
                onSuccess={() => {
                    setCommissionModalVisible(false);
                    setEditingCommission(null);
                    loadCommissions();
                }}
                employeeId={employeeId}
                editingRecord={editingCommission}
                dateOfJoining={employeeData?.date_of_joining}
            />

            <OtherPaymentModal
                visible={otherPaymentModalVisible}
                onCancel={() => {
                    setOtherPaymentModalVisible(false);
                    setEditingOtherPayment(null);
                }}
                onSuccess={() => {
                    setOtherPaymentModalVisible(false);
                    setEditingOtherPayment(null);
                    loadOtherPayments();
                }}
                employeeId={employeeId}
                editingRecord={editingOtherPayment}
                dateOfJoining={employeeData?.date_of_joining}
            />

            <DeductionModal
                visible={deductionModalVisible}
                onCancel={() => {
                    setDeductionModalVisible(false);
                    setEditingDeduction(null);
                }}
                onSuccess={() => {
                    setDeductionModalVisible(false);
                    setEditingDeduction(null);
                    loadDeductions();
                }}
                employeeId={employeeId}
                editingRecord={editingDeduction}
                dateOfJoining={employeeData?.date_of_joining}
            />

            <SalaryPreviewModal
                visible={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                employeeId={employeeId}
                currentBasicSalary={currentBasicSalary}
                dateOfJoining={employeeData?.date_of_joining}
            />
        </div>
    );
};

export default SalaryInfoTab;
